import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Avatar
} from '@mui/material';
import {
  Search,
  Add,
  Edit,
  Delete,
  Image,
  VolumeUp,
  Close,
  Category as CategoryIcon,
  PhotoLibrary,
  AudioFile,
  AutoAwesome,
  PlayArrow,
  Stop,
  Upload,
  Refresh,
  RestoreFromTrash,
  ToggleOn,
  ToggleOff
} from '@mui/icons-material';
import { Item, ItemInput } from '../types/item';
import { Category } from '../types/category';
import itemService from '../services/itemService';
import categoryService from '../services/categoryService';
import aiService from '../services/aiService';

interface ItemFormData extends ItemInput {
  id?: string;
}

export const ItemsPage: React.FC = () => {
  const [items, setItems] = useState<Item[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [formData, setFormData] = useState<ItemFormData>({
    code: '',
    name: '',
    description: '',
    categoryCode: '',
    subCategoryCode: '',
    status: 1,
    position: 0,
    associatedCategoryCodes: '',
    audioText: ''
  });
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{url: string, name: string} | null>(null);
  // New state for enhanced functionality
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [audioPreview, setAudioPreview] = useState<string | null>(null);
  const [audioPlayer, setAudioPlayer] = useState<HTMLAudioElement | null>(null);
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [itemJustCreated, setItemJustCreated] = useState(false);

  // Utility function to clean sound options for GraphQL input (remove __typename)
  const cleanSoundOptions = (soundOptions: any[] = []) => {
    return soundOptions.map(option => ({
      voiceId: option.voiceId,
      soundData: option.soundData,
      soundFileName: option.soundFileName,
      soundContentType: option.soundContentType,
      soundGenerated: option.soundGenerated,
      soundGeneratedAt: option.soundGeneratedAt
    }));
  };

  // Utility function to clean form data for GraphQL input (remove id and other non-input fields)
  const cleanFormData = (data: ItemFormData) => {
    const { id, ...cleanData } = data;
    return cleanData;
  };

  // Load data on component mount
  useEffect(() => {
    loadItems();
    loadCategories();
  }, []);

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await itemService.getItems();
      setItems(data);
    } catch (err: any) {
      setError('Failed to load items: ' + (err.message || 'Unknown error'));
      console.error('Error loading items:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const data = await categoryService.getCategories();
      setCategories(data);
    } catch (err: any) {
      console.error('Error loading categories:', err);
    }
  };

  const showSnackbar = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  // Handle opening image in modal
  const handleImagePreview = (imageUrl: string, itemName: string) => {
    setSelectedImage({ url: imageUrl, name: itemName });
    setImageModalOpen(true);
  };

  // Handle audio preview
  const handleAudioPreview = (audioUrl: string, itemId: string) => {
    if (playingAudio === itemId) {
      // Stop current audio
      if (audioPlayer) {
        audioPlayer.pause();
        audioPlayer.currentTime = 0;
        setAudioPlayer(null);
      }
      setPlayingAudio(null);
    } else {
      // Stop any currently playing audio
      if (audioPlayer) {
        audioPlayer.pause();
        audioPlayer.currentTime = 0;
      }
      
      // Start new audio
      const audio = new Audio(audioUrl);
      audio.onended = () => {
        setPlayingAudio(null);
        setAudioPlayer(null);
      };
      audio.onerror = () => {
        console.error('Error playing audio');
        setPlayingAudio(null);
        setAudioPlayer(null);
        setError('Failed to play audio');
      };
      
      audio.play().catch(err => {
        console.error('Error playing audio:', err);
        setError('Failed to play audio');
        setPlayingAudio(null);
        setAudioPlayer(null);
      });
      
      setAudioPlayer(audio);
      setPlayingAudio(itemId);
    }
  };

  // Enhanced image generation
  const handleGenerateImage = async (itemId?: string, targetItemOverride?: Item) => {
    const targetItem = targetItemOverride || (itemId ? items.find(i => i.id === itemId) : selectedItem);
    if (!targetItem) {
      setError('No item selected for image generation');
      return;
    }

    try {
      setGeneratingImage(true);
      setError(null);
      
      // Step 1: Generate the image content only
      const result = await aiService.generateImage({
        prompt: `A simple, colorful illustration of ${targetItem.name} suitable for children's educational materials`,
        size: '1024x1024',
        quality: 'standard'
      }, targetItem.code, 'item');
      
      if (result.success && result.imageUrl) {
        // Step 2: Extract base64 data and update the item
        const base64Data = result.imageUrl.replace('data:image/png;base64,', '');
        
        // Create updated item data with the new image, preserving existing sound options
        const updateData = {
          code: targetItem.code,
          name: targetItem.name,
          description: targetItem.description || '',
          categoryCode: targetItem.categoryCode,
          subCategoryCode: targetItem.subCategoryCode || '',
          status: targetItem.status,
          position: targetItem.position,
          associatedCategoryCodes: targetItem.associatedCategoryCodes || '',
          // Preserve existing audioText to prevent it from being overwritten
          audioText: targetItem.audioText || '',
          imageData: base64Data,
          // Generate a default filename when creating new image
          imageFileName: `${targetItem.code}_image_${new Date().getTime()}.png`,
          imageGenerated: true,
          imageGeneratedAt: new Date().toISOString(),
          // Preserve existing sound options to avoid overwriting audio data
          // Remove __typename fields for GraphQL input
          soundOptions: cleanSoundOptions(targetItem.soundOptions)
        };
        
        // Update the item with the new image
        await itemService.updateItem(targetItem.id, updateData);
        
        // Reload items to get updated data with new image
        await loadItems();
        
        // If we're in edit mode, refresh the form data with the latest item data
        if (selectedItem && selectedItem.id === targetItem.id) {
          const updatedItems = await itemService.getItems();
          const updatedItem = updatedItems.find((i: Item) => i.id === targetItem.id);
          if (updatedItem) {
            setSelectedItem(updatedItem);
            // Update form data with the new image data
            setFormData(prev => ({
              ...prev,
              imageData: updatedItem.imageData,
              imageFileName: updatedItem.imageFileName,
              imageGenerated: updatedItem.imageGenerated
            }));
            // Update image preview
            if (updatedItem.imageData) {
              setImagePreview(`data:image/png;base64,${updatedItem.imageData}`);
            }
          }
        }
        
        showSnackbar('Image generated and saved successfully');
      } else {
        console.error('🎨 Image generation failed:', result.message);
        setError(result.message || 'Failed to generate image');
      }
      
    } catch (err: any) {
      console.error('🎨 Error generating image:', err);
      setError('Failed to generate image: ' + (err.message || 'Unknown error'));
    } finally {
      setGeneratingImage(false);
    }
  };

  // Enhanced audio generation - generates for all available voices
  const handleGenerateAudio = async (itemId?: string, audioText?: string, targetItemOverride?: Item) => {
    // If we have an itemId, always fetch the latest item data to ensure we have current image data
    let targetItem = targetItemOverride;
    if (itemId && !targetItemOverride) {
      targetItem = items.find(i => i.id === itemId);
    }
    if (!targetItem && selectedItem) {
      targetItem = selectedItem;
    }
    
    // If we still don't have a target item, fetch it from the server to get the latest data
    if (!targetItem && itemId) {
      console.log('🎵 Fetching latest item data to ensure we have current image data');
      const latestItems = await itemService.getItems();
      targetItem = latestItems.find((i: Item) => i.id === itemId);
    }
    
    if (!targetItem) {
      setError('No item selected for audio generation');
      return;
    }
    
    const textToSpeak = audioText || formData.audioText || targetItem.name;
    if (!textToSpeak?.trim()) {
      setError('Please provide text for audio generation. Fill in the "Audio Text" field or ensure the item name is provided.');
      return;
    }

    try {
      setGeneratingAudio(true);
      setError(null);
      
      // Generate audio for ALL voices in one atomic call (all-or-nothing quota enforcement)
      const { results: voiceResults, errorMessage } = await aiService.generateAudioAllVoices(textToSpeak.trim());

      if (errorMessage) {
        setError(errorMessage);
        return;
      }

      if (voiceResults.length === 0) {
        setError('No audio was generated. Please try again.');
        return;
      }

      const successfulGenerations = voiceResults.map(r => ({
        voiceId: r.voiceId,
        voiceName: r.voiceName,
        soundData: r.audioBase64,
        soundContentType: 'audio/mpeg',
        soundGenerated: true,
        soundGeneratedAt: new Date().toISOString(),
        success: true
      }));

      if (successfulGenerations.length > 0) {
        // Create sound options for all successful generations
        const soundOptions = successfulGenerations.map(gen => ({
          voiceId: gen.voiceId,
          soundData: gen.soundData || '',
          soundFileName: `${targetItem!.code}_${gen.voiceId}_audio_${new Date().getTime()}.mp3`,
          soundContentType: gen.soundContentType || 'audio/mpeg',
          soundGenerated: gen.soundGenerated || true,
          soundGeneratedAt: gen.soundGeneratedAt || new Date().toISOString()
        }));

        // Create updated item data with all generated audio, preserving existing image data
        const updateData = {
          code: targetItem!.code,
          name: targetItem!.name,
          description: targetItem!.description || '',
          categoryCode: targetItem!.categoryCode,
          subCategoryCode: targetItem!.subCategoryCode || '',
          status: targetItem!.status,
          position: targetItem!.position,
          associatedCategoryCodes: targetItem!.associatedCategoryCodes || '',
          // Preserve the existing audioText to prevent it from being overwritten
          audioText: targetItem!.audioText || '',
          // Clean sound options - remove __typename for GraphQL input
          soundOptions: soundOptions.map(option => ({
            voiceId: option.voiceId,
            soundData: option.soundData,
            soundFileName: option.soundFileName,
            soundContentType: option.soundContentType,
            soundGenerated: option.soundGenerated,
            soundGeneratedAt: option.soundGeneratedAt
          })),
          // Preserve existing image data to avoid overwriting image
          imageData: targetItem!.imageData,
          imageFileName: targetItem!.imageFileName,
          imageGenerated: targetItem!.imageGenerated || false,
          imageGeneratedAt: targetItem!.imageGeneratedAt
        };
        
        // Update the item with all the generated audio
        await itemService.updateItem(targetItem!.id, updateData);
        
        // Reload items to get updated data with new audio
        await loadItems();
        
        // If we're in edit mode, refresh the form data with the latest item data
        if (selectedItem && targetItem && selectedItem.id === targetItem!.id) {
          const targetItemId = targetItem!.id; // Store the ID to avoid undefined issues
          const updatedItems = await itemService.getItems();
          const updatedItem = updatedItems.find((i: Item) => i.id === targetItemId);
          if (updatedItem) {
            setSelectedItem(updatedItem);
            // Update form data with the new sound options
            setFormData(prev => ({
              ...prev,
              soundOptions: updatedItem.soundOptions || []
            }));
            console.log('🎵 Updated selectedItem state and form data. SoundOptions count:', updatedItem.soundOptions?.length || 0);
            // Update audio preview with the first sound option
            if (updatedItem.soundOptions?.[0]?.soundData) {
              setAudioPreview(`data:${updatedItem.soundOptions[0].soundContentType || 'audio/mpeg'};base64,${updatedItem.soundOptions[0].soundData}`);
              console.log('🎵 Updated audio preview');
            } else {
              console.warn('🎵 Updated item has no sound options');
            }
          }
        }
        
        const message = `Audio generated successfully for all ${successfulGenerations.length} voice(s)!`;
          
        showSnackbar(message);
      } else {
        setError('Failed to generate audio for any voice');
      }
      
    } catch (err: any) {
      console.error('Error generating audio:', err);
      setError('Failed to generate audio: ' + (err.message || 'Unknown error'));
    } finally {
      setGeneratingAudio(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {};
    
    if (!formData.code.trim()) {
      errors.code = 'Code is required';
    }
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (!formData.categoryCode.trim()) {
      errors.categoryCode = 'Category is required';
    }
    
    // Require audio text for new items (when no existing sound options)
    if (!selectedItem && (!formData.audioText || !formData.audioText.trim())) {
      errors.audioText = 'Audio text is required for new items';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      description: '',
      categoryCode: '',
      subCategoryCode: '',
      status: 1,
      position: 0,
      associatedCategoryCodes: '',
      audioText: '',
      imageData: undefined,
      imageFileName: undefined,
      imageGenerated: false,
      soundOptions: []
    });
    setFormErrors({});
    setSelectedItem(null);
    setImagePreview(null);
    setAudioPreview(null);
    setItemJustCreated(false);
    // Stop any playing audio
    if (audioPlayer) {
      audioPlayer.pause();
      audioPlayer.currentTime = 0;
      setAudioPlayer(null);
    }
    setPlayingAudio(null);
  };

  const handleCreate = () => {
    resetForm();
    setOpenDialog(true);
  };

  const handleEdit = (item: Item) => {
    setSelectedItem(item);
    setItemJustCreated(false); // This is an edit, not a new creation
    setFormData({
      id: item.id,
      code: item.code,
      name: item.name,
      description: item.description || '',
      categoryCode: item.categoryCode,
      subCategoryCode: item.subCategoryCode || '',
      status: item.status,
      position: item.position,
      associatedCategoryCodes: item.associatedCategoryCodes || '',
      audioText: item.audioText || '', // Use the actual audioText from the item
      // Preserve existing image and audio data
      imageData: item.imageData,
      imageFileName: item.imageFileName,
      imageGenerated: item.imageGenerated,
      soundOptions: item.soundOptions || []
    });
    
    // Set up image preview if available
    if (item.imageData) {
      setImagePreview(`data:image/png;base64,${item.imageData}`);
    } else {
      setImagePreview(null);
    }
    
    // Set up audio preview if available
    if (item.soundOptions?.[0]?.soundData) {
      setAudioPreview(`data:${item.soundOptions[0].soundContentType || 'audio/mpeg'};base64,${item.soundOptions[0].soundData}`);
    } else {
      setAudioPreview(null);
    }
    
    setOpenDialog(true);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      
      if (selectedItem) {
        // Update existing item
        // Clean formData to remove 'id' and other non-input fields
        const updateData = cleanFormData(formData);
        await itemService.updateItem(selectedItem.id, updateData);
        showSnackbar('Item updated successfully!');
        setOpenDialog(false);
        resetForm();
        await loadItems();
      } else {
        // Create new item
        const createData = cleanFormData(formData);
        const createdItem = await itemService.createItem(createData);
        showSnackbar('Item created successfully!');
        
        // Set the created item as selected to enable edit mode
        setSelectedItem(createdItem);
        setItemJustCreated(true); // Mark that this item was just created
        
        // Update form data with the created item's complete data
        setFormData({
          id: createdItem.id,
          code: createdItem.code,
          name: createdItem.name,
          description: createdItem.description || '',
          categoryCode: createdItem.categoryCode,
          subCategoryCode: createdItem.subCategoryCode || '',
          status: createdItem.status,
          position: createdItem.position,
          associatedCategoryCodes: createdItem.associatedCategoryCodes || '',
          audioText: createdItem.audioText || '',
          imageData: createdItem.imageData,
          imageFileName: createdItem.imageFileName,
          imageGenerated: createdItem.imageGenerated,
          soundOptions: createdItem.soundOptions || []
        });
        
        // Update previews if media exists
        if (createdItem.imageData) {
          setImagePreview(`data:image/png;base64,${createdItem.imageData}`);
        }
        if (createdItem.soundOptions?.[0]?.soundData) {
          setAudioPreview(`data:${createdItem.soundOptions[0].soundContentType || 'audio/mpeg'};base64,${createdItem.soundOptions[0].soundData}`);
        }
        
        // Reload items to update the list
        await loadItems();
        
        // Auto-generate image and audio for the new item
        try {
          // Generate image first - pass the created item directly
          await handleGenerateImage(undefined, createdItem);
          
          // Generate audio - the function will fetch latest item data automatically
          const audioText = formData.audioText || formData.name;
          if (audioText?.trim()) {
            await handleGenerateAudio(createdItem.id, audioText.trim());
          }
          
          // After generation, fetch the updated item and refresh the modal
          const updatedItems = await itemService.getItems();
          const finalUpdatedItem = updatedItems.find((i: Item) => i.id === createdItem.id);
          if (finalUpdatedItem) {
            setSelectedItem(finalUpdatedItem);
            // Update form data with the final item data including generated media
            setFormData({
              id: finalUpdatedItem.id,
              code: finalUpdatedItem.code,
              name: finalUpdatedItem.name,
              description: finalUpdatedItem.description || '',
              categoryCode: finalUpdatedItem.categoryCode,
              subCategoryCode: finalUpdatedItem.subCategoryCode || '',
              status: finalUpdatedItem.status,
              position: finalUpdatedItem.position,
              associatedCategoryCodes: finalUpdatedItem.associatedCategoryCodes || '',
              audioText: finalUpdatedItem.audioText || '',
              imageData: finalUpdatedItem.imageData,
              imageFileName: finalUpdatedItem.imageFileName,
              imageGenerated: finalUpdatedItem.imageGenerated,
              soundOptions: finalUpdatedItem.soundOptions || []
            });
            
            // Update previews with generated media
            if (finalUpdatedItem.imageData) {
              setImagePreview(`data:image/png;base64,${finalUpdatedItem.imageData}`);
            }
            if (finalUpdatedItem.soundOptions?.[0]?.soundData) {
              setAudioPreview(`data:${finalUpdatedItem.soundOptions[0].soundContentType || 'audio/mpeg'};base64,${finalUpdatedItem.soundOptions[0].soundData}`);
            }
          }
        } catch (genErr: any) {
          console.error('Error auto-generating content:', genErr);
          // Don't show error for auto-generation failures, just log them
        }
        
        // Keep the dialog open so user can see the generated content
        // They can manually close it when done
      }
    } catch (err: any) {
      setError('Failed to save item: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item: Item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.name}"?`)) {
      return;
    }

    try {
      // Soft delete: Set status to -1 instead of actually deleting
      const updateData = {
        code: item.code,
        name: item.name,
        description: item.description || '',
        categoryCode: item.categoryCode,
        subCategoryCode: item.subCategoryCode || '',
        status: -1, // Mark as deleted
        position: item.position,
        associatedCategoryCodes: item.associatedCategoryCodes || '',
        // Preserve existing audioText and image data
        audioText: item.audioText || '',
        imageData: item.imageData,
        imageFileName: item.imageFileName,
        imageGenerated: item.imageGenerated || false,
        imageGeneratedAt: item.imageGeneratedAt,
        // Clean sound options - remove __typename for GraphQL input
        soundOptions: cleanSoundOptions(item.soundOptions)
      };
      
      await itemService.updateItem(item.id, updateData);
      showSnackbar('Item deleted successfully!');
      await loadItems();
    } catch (err: any) {
      setError('Failed to delete item: ' + (err.message || 'Unknown error'));
    }
  };

  const handleToggleStatus = async (item: Item) => {
    if (item.status === -1) {
      // Can't toggle deleted items
      return;
    }

    const newStatus = item.status === 1 ? 0 : 1;
    const statusText = newStatus === 1 ? 'activate' : 'deactivate';
    
    if (!window.confirm(`Are you sure you want to ${statusText} "${item.name}"?`)) {
      return;
    }

    try {
      const updateData = {
        code: item.code,
        name: item.name,
        description: item.description || '',
        categoryCode: item.categoryCode,
        subCategoryCode: item.subCategoryCode || '',
        status: newStatus,
        position: item.position,
        associatedCategoryCodes: item.associatedCategoryCodes || '',
        // Preserve existing audioText and image data
        audioText: item.audioText || '',
        imageData: item.imageData,
        imageFileName: item.imageFileName,
        imageGenerated: item.imageGenerated || false,
        imageGeneratedAt: item.imageGeneratedAt,
        // Clean sound options - remove __typename for GraphQL input
        soundOptions: cleanSoundOptions(item.soundOptions)
      };
      
      await itemService.updateItem(item.id, updateData);
      showSnackbar(`Item ${newStatus === 1 ? 'activated' : 'deactivated'} successfully!`);
      await loadItems();
    } catch (err: any) {
      setError(`Failed to ${statusText} item: ` + (err.message || 'Unknown error'));
    }
  };

  const handleRestore = async (item: Item) => {
    if (!window.confirm(`Are you sure you want to restore "${item.name}"?`)) {
      return;
    }

    try {
      // Restore item: Set status back to active
      const updateData = {
        code: item.code,
        name: item.name,
        description: item.description || '',
        categoryCode: item.categoryCode,
        subCategoryCode: item.subCategoryCode || '',
        status: 1, // Mark as active
        position: item.position,
        associatedCategoryCodes: item.associatedCategoryCodes || '',
        // Preserve existing audioText and image data
        audioText: item.audioText || '',
        imageData: item.imageData,
        imageFileName: item.imageFileName,
        imageGenerated: item.imageGenerated || false,
        imageGeneratedAt: item.imageGeneratedAt,
        // Clean sound options - remove __typename for GraphQL input
        soundOptions: cleanSoundOptions(item.soundOptions)
      };
      
      await itemService.updateItem(item.id, updateData);
      showSnackbar('Item restored successfully!');
      await loadItems();
    } catch (err: any) {
      setError('Failed to restore item: ' + (err.message || 'Unknown error'));
    }
  };

  // Filter items based on search and filters
  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && item.status === 1) ||
                         (statusFilter === 'inactive' && item.status === 0);

    const matchesCategory = categoryFilter === 'all' || item.categoryCode === categoryFilter;
    
    return matchesSearch && matchesStatus && matchesCategory;
  });

  // Paginated items
  const paginatedItems = filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const getCategoryName = (categoryCode: string) => {
    const category = categories.find(c => c.code === categoryCode);
    return category?.name || categoryCode;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Action Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            placeholder="Search items..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0); // Reset to first page when search changes
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => {
                setStatusFilter(e.target.value as 'all' | 'active' | 'inactive');
                setPage(0); // Reset to first page when filter changes
              }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              label="Category"
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setPage(0); // Reset to first page when filter changes
              }}
            >
              <MenuItem value="all">All Categories</MenuItem>
              {/* Top-level categories */}
              {categories
                .filter(category => !category.parentCode)
                .map((category) => (
                <MenuItem key={category.code} value={category.code}>
                  📁 {category.name}
                </MenuItem>
              ))}
              {/* Subcategories */}
              {categories
                .filter(category => category.parentCode)
                .map((category) => (
                <MenuItem key={category.code} value={category.code}>
                  └── {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {(searchTerm || statusFilter !== 'all' || categoryFilter !== 'all') && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setCategoryFilter('all');
                setPage(0);
              }}
            >
              Clear Filters
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreate}
            disabled={loading}
          >
            Add Item
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    backgroundColor: 'primary.light',
                    color: 'primary.main',
                    mr: 2
                  }}
                >
                  <CategoryIcon />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {items.filter(i => i.status !== -1).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Items
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    backgroundColor: 'success.light',
                    color: 'success.main',
                    mr: 2
                  }}
                >
                  <CategoryIcon />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {items.filter(i => i.status === 1).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Items
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    backgroundColor: 'info.light',
                    color: 'info.main',
                    mr: 2
                  }}
                >
                  <Image />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {items.filter(i => i.imageGenerated && i.imageData && i.status !== -1).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    With Images
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    backgroundColor: 'warning.light',
                    color: 'warning.main',
                    mr: 2
                  }}
                >
                  <VolumeUp />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {items.filter(i => 
                      i.soundOptions && i.soundOptions.length > 0 && i.soundOptions.some(option => option.soundGenerated) && i.status !== -1
                    ).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    With Audio
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Items Table */}
      <Paper elevation={2}>
          <Box sx={{ p: 3 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table sx={{ 
                    '& th, & td': { 
                      verticalAlign: 'top',
                      padding: '12px 16px'
                    },
                    '& th': { 
                      backgroundColor: 'grey.50',
                      fontWeight: 600
                    },
                    '& tbody tr:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: '100px' }}>Code</TableCell>
                        <TableCell sx={{ minWidth: '150px' }}>Name</TableCell>
                        <TableCell sx={{ width: '80px' }}>Image</TableCell>
                        <TableCell sx={{ width: '120px' }}>Category</TableCell>
                        <TableCell sx={{ width: '80px' }}>Status</TableCell>
                        <TableCell sx={{ width: '180px' }}>Assets</TableCell>
                        <TableCell align="right" sx={{ width: '150px' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedItems.map((item) => (
                        <TableRow key={item.id} hover>
                          <TableCell sx={{ verticalAlign: 'top', width: '100px' }}>
                            <Chip label={item.code} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {item.name}
                            </Typography>
                            {item.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {item.description}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            {item.imageData ? (
                              <Box
                                component="img"
                                src={`data:image/png;base64,${item.imageData}`}
                                alt={item.name}
                                sx={{
                                  width: 60,
                                  height: 60,
                                  objectFit: 'cover',
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  cursor: 'pointer',
                                  transition: 'transform 0.2s',
                                  '&:hover': {
                                    transform: 'scale(1.1)',
                                  }
                                }}
                                onClick={() => handleImagePreview(
                                  `data:image/png;base64,${item.imageData}`,
                                  item.name
                                )}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: 60,
                                  height: 60,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: 'grey.100',
                                  borderRadius: 1,
                                  border: '1px dashed',
                                  borderColor: 'grey.300'
                                }}
                              >
                                <Image sx={{ color: 'grey.400', fontSize: 24 }} />
                              </Box>
                            )}
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <Chip
                              label={getCategoryName(item.categoryCode)}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <Chip
                              label={
                                item.status === 1 ? 'Active' : 
                                item.status === 0 ? 'Inactive' : 
                                item.status === -1 ? 'Deleted' : `Unknown (${item.status})`
                              }
                              color={
                                item.status === 1 ? 'success' : 
                                item.status === 0 ? 'warning' : 
                                item.status === -1 ? 'error' : 'default'
                              }
                              size="small"
                              variant={item.status === 0 ? 'filled' : 'outlined'}
                            />
                          </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {(item.imageGenerated || item.imageData) ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Chip icon={<Image />} label="Image" size="small" variant="outlined" color="info" />
                              {item.imageData && (
                                <IconButton size="small" onClick={() => handleImagePreview(
                                  `data:image/png;base64,${item.imageData}`,
                                  item.name
                                )} sx={{ ml: 0.5 }}>
                                  <PhotoLibrary />
                                </IconButton>
                              )}
                            </Box>
                          ) : (
                            <Chip icon={<Image />} label="No Image" size="small" variant="outlined" color="default" />
                          )}
                          
                          {(item.soundOptions && item.soundOptions.length > 0 && item.soundOptions.some(option => option.soundGenerated || option.soundData)) ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Chip icon={<VolumeUp />} label="Audio" size="small" variant="outlined" color="warning" />
                              <IconButton size="small" onClick={() => {
                                const soundOption = item.soundOptions?.[0];
                                if (soundOption?.soundData) {
                                  handleAudioPreview(
                                    `data:${soundOption.soundContentType || 'audio/mpeg'};base64,${soundOption.soundData}`,
                                    item.id
                                  );
                                }
                              }} sx={{ ml: 0.5 }}>
                                {playingAudio === item.id ? <Stop /> : <PlayArrow />}
                              </IconButton>
                            </Box>
                          ) : (
                            <Chip icon={<VolumeUp />} label="No Audio" size="small" variant="outlined" color="default" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          {item.status === -1 ? (
                            // Actions for deleted items
                            <IconButton
                              size="small"
                              onClick={() => handleRestore(item)}
                              color="success"
                              title="Restore item"
                            >
                              <RestoreFromTrash />
                            </IconButton>
                          ) : (
                            // Actions for active/inactive items
                            <>
                              <IconButton
                                size="small"
                                onClick={() => handleToggleStatus(item)}
                                color={item.status === 1 ? "success" : "default"}
                                title={item.status === 1 ? "Deactivate item" : "Activate item"}
                              >
                                {item.status === 1 ? <ToggleOn /> : <ToggleOff />}
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleGenerateImage(item.id)}
                                title="Generate Image"
                                disabled={generatingImage}
                                color="info"
                              >
                                {generatingImage ? <CircularProgress size={16} /> : <AutoAwesome />}
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleGenerateAudio(item.id, item.name)}
                                title="Generate Audio"
                                disabled={generatingAudio}
                                color="warning"
                              >
                                {generatingAudio ? <CircularProgress size={16} /> : <AutoAwesome />}
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(item)}
                                color="primary"
                              >
                                <Edit />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(item)}
                                color="error"
                                title="Delete item"
                              >
                                <Delete />
                              </IconButton>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={filteredItems.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(event, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                />
              </>
            )}
          </Box>
        </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">
                {selectedItem 
                  ? `Edit Item: ${selectedItem.name}` 
                  : 'Add New Item'
                }
              </Typography>
              {selectedItem && (
                <Typography variant="caption" color="text.secondary">
                  Add images and audio to enhance your item
                </Typography>
              )}
            </Box>
            <IconButton onClick={() => setOpenDialog(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Show success alert when item was just created */}
            {selectedItem && itemJustCreated && !error && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Item "{selectedItem.name}" created successfully!
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Image and audio are being generated automatically. You can close this dialog when ready or generate additional content below.
                  </Typography>
                  
                  {/* Status indicators */}
                  <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {selectedItem.imageData ? (
                        <>
                          <Image sx={{ color: 'success.main', fontSize: 16 }} />
                          <Typography variant="caption" color="success.main">Image Ready</Typography>
                        </>
                      ) : generatingImage ? (
                        <>
                          <CircularProgress size={14} />
                          <Typography variant="caption" color="info.main">Generating Image...</Typography>
                        </>
                      ) : (
                        <>
                          <Image sx={{ color: 'text.secondary', fontSize: 16 }} />
                          <Typography variant="caption" color="text.secondary">Image Pending</Typography>
                        </>
                      )}
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {selectedItem.soundOptions && selectedItem.soundOptions.length > 0 ? (
                        <>
                          <VolumeUp sx={{ color: 'success.main', fontSize: 16 }} />
                          <Typography variant="caption" color="success.main">
                            Audio Ready ({selectedItem.soundOptions.length} voices)
                          </Typography>
                        </>
                      ) : generatingAudio ? (
                        <>
                          <CircularProgress size={14} />
                          <Typography variant="caption" color="info.main">Generating Audio...</Typography>
                        </>
                      ) : (
                        <>
                          <VolumeUp sx={{ color: 'text.secondary', fontSize: 16 }} />
                          <Typography variant="caption" color="text.secondary">Audio Pending</Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Alert>
            )}
            
            {/* Basic Information */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>Basic Information</Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    error={!!formErrors.code}
                    helperText={formErrors.code}
                    disabled={!!selectedItem} // Don't allow changing code when editing
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    error={!!formErrors.name}
                    helperText={formErrors.name}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!formErrors.categoryCode}>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={formData.categoryCode}
                      label="Category"
                      onChange={(e) => {
                        const newCategoryCode = e.target.value;
                        setFormData({ 
                          ...formData, 
                          categoryCode: newCategoryCode,
                          // Clear subcategory if it doesn't belong to the new main category
                          subCategoryCode: formData.subCategoryCode && 
                            categories.find(cat => cat.code === formData.subCategoryCode)?.parentCode !== newCategoryCode 
                            ? '' 
                            : formData.subCategoryCode
                        });
                      }}
                    >
                      {categories
                        .filter(category => !category.parentCode) // Only top-level categories (no parent)
                        .map((category) => (
                        <MenuItem key={category.code} value={category.code}>
                          {category.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {formErrors.categoryCode && (
                      <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                        {formErrors.categoryCode}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, ml: 2 }}>
                      Select the main category for this item
                    </Typography>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Sub Category</InputLabel>
                    <Select
                      value={formData.subCategoryCode}
                      label="Sub Category"
                      onChange={(e) => setFormData({ ...formData, subCategoryCode: e.target.value })}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {categories
                        .filter(category => {
                          // Show subcategories that belong to the selected main category, or all if no main category selected
                          return category.parentCode && 
                                 (!formData.categoryCode || category.parentCode === formData.categoryCode);
                        })
                        .map((category) => (
                        <MenuItem key={category.code} value={category.code}>
                          {category.name}
                          {formData.categoryCode !== category.parentCode && (
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              (from {category.parentCode})
                            </Typography>
                          )}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, ml: 2 }}>
                      Optional: Select a subcategory within the main category
                    </Typography>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={formData.status}
                      label="Status"
                      onChange={(e) => setFormData({ ...formData, status: Number(e.target.value) })}
                    >
                      <MenuItem value={1}>Active</MenuItem>
                      <MenuItem value={0}>Inactive</MenuItem>
                      <MenuItem value={-1}>Deleted</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Position"
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: Number(e.target.value) })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Associated Category Codes"
                    value={formData.associatedCategoryCodes}
                    onChange={(e) => setFormData({ ...formData, associatedCategoryCodes: e.target.value })}
                    helperText="Comma-separated list of related category codes"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required={!selectedItem} // Required for new items
                    label="Audio Text"
                    value={formData.audioText}
                    onChange={(e) => setFormData({ ...formData, audioText: e.target.value })}
                    helperText={formErrors.audioText || "Text to be converted to speech for this item"}
                    error={!!formErrors.audioText}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Image Management */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>Image</Typography>
              
              {/* Image state indicators */}
              {!selectedItem && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Image will be generated automatically when you create the item
                </Alert>
              )}
              
              {/* Image generation status */}
              {generatingImage && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2">
                      Generating image... This may take a few moments.
                    </Typography>
                  </Box>
                </Alert>
              )}
              
              {/* Image preview */}
              {imagePreview && (
                <Box sx={{ mb: 2 }}>
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    style={{ 
                      maxWidth: '100%',
                      maxHeight: '200px',
                      objectFit: 'contain',
                      border: '1px solid #ccc',
                      borderRadius: '8px'
                    }}
                  />
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={generatingImage ? <CircularProgress size={16} /> : <AutoAwesome />}
                  onClick={() => handleGenerateImage()}
                  disabled={generatingImage || !formData.name.trim() || !selectedItem}
                  sx={{ flex: 1, minWidth: '150px' }}
                  title={!selectedItem ? "Images are automatically generated when creating items" : "Generate image using AI"}
                >
                  {generatingImage ? 'Generating Image...' : 'Generate Image'}
                </Button>
              </Box>
            </Box>

            {/* Audio Management */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>Audio</Typography>
              
              {/* Audio state indicators */}
              {!selectedItem && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Audio will be generated automatically when you create the item
                </Alert>
              )}
              
              {/* Audio generation status */}
              {generatingAudio && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2">
                      Generating audio for all voices... This may take a few moments.
                    </Typography>
                  </Box>
                </Alert>
              )}
              
              {/* Audio text validation */}
              {selectedItem && !formData.audioText?.trim() && !formData.name?.trim() && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Please enter audio text or ensure the item name is filled to generate audio.
                  </Typography>
                </Alert>
              )}
              
              {/* Audio preview controls */}
              {audioPreview && (
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={playingAudio ? <Stop /> : <PlayArrow />}
                    onClick={() => handleAudioPreview(audioPreview, 'preview')}
                    sx={{ mb: 1 }}
                  >
                    {playingAudio ? 'Stop Audio' : 'Play Preview'}
                  </Button>
                </Box>
              )}
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={generatingAudio ? <CircularProgress size={16} /> : <AutoAwesome />}
                  onClick={() => handleGenerateAudio(undefined, formData.audioText || formData.name)}
                  disabled={generatingAudio || !(formData.audioText?.trim() || formData.name?.trim()) || !selectedItem}
                  sx={{ flex: 1, minWidth: '150px' }}
                  title={!selectedItem ? "Audio is automatically generated when creating items" : "Generate audio using AI"}
                >
                  {generatingAudio ? 'Generating Audio...' : 'Generate Audio for All Voices'}
                </Button>
              </Box>
              
              {/* Help text for audio generation */}
              {selectedItem && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Audio will be generated for all available voices. Use the "Audio Text" field above to specify custom text.
                  {selectedItem.soundOptions && selectedItem.soundOptions.length > 0 && (
                    <> Currently has audio for {selectedItem.soundOptions.length} voice(s).</>
                  )}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Box>
              {!selectedItem && (
                <Typography variant="caption" color="text.secondary">
                  Images and audio will be generated automatically when you create the item
                </Typography>
              )}
              {selectedItem && (
                <Typography variant="caption" color="text.secondary">
                  Content generated automatically. Close when ready or generate additional content above.
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                onClick={() => {
                  setOpenDialog(false);
                  resetForm();
                }} 
                disabled={saving}
              >
                {selectedItem ? 'Close' : 'Cancel'}
              </Button>
              {!selectedItem && (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={saving}
                >
                  {saving ? <CircularProgress size={20} /> : 'Create Item'}
                </Button>
              )}
              {selectedItem && (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={saving}
                >
                  {saving ? <CircularProgress size={20} /> : 'Update Item'}
                </Button>
              )}
            </Box>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Image Preview Modal */}
      <Dialog
        open={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            boxShadow: 'none',
          }
        }}
      >
        <DialogTitle sx={{ color: 'white', pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {selectedImage?.name} - Image Preview
            </Typography>
            <IconButton 
              onClick={() => setImageModalOpen(false)}
              sx={{ color: 'white' }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {selectedImage && (
            <Box
              component="img"
              src={selectedImage.url}
              alt={selectedImage.name}
              sx={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
                borderRadius: 1,
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Container>
  );
};
